import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { ApiResponse, PaginatedResponse } from "@/lib/types";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL;

export interface Tag {
  _id: string;
  name: string;
  slug: string;
  details: string;
  image?: string;
  icon?: {
    name: string;
    url: string;
  };
  type?: string;
  createdAt: string;
  updatedAt: string;
}

export const tagsApi = createApi({
  reducerPath: "tagsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    credentials: "include",
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("accessToken");
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["Tags"],
  endpoints: (builder) => ({
    createTag: builder.mutation<ApiResponse<Tag>, FormData>({
      query: (data) => ({
        url: "/tag/create-tag",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Tags"],
    }),
    getAllTags: builder.query<
      PaginatedResponse<Tag>,
      { limit?: number; searchTerm?: string; page?: number } | void
    >({
      query: (params) => ({
        url: "/tag",
        params: params || undefined,
      }),
      providesTags: ["Tags"],
    }),
    getTagById: builder.query<ApiResponse<Tag>, string>({
      query: (id) => `/tag/${id}`,
      providesTags: ["Tags"],
    }),
    updateTag: builder.mutation<
      ApiResponse<Tag>,
      { id: string; data: FormData }
    >({
      query: ({ id, data }) => ({
        url: `/tag/update-tag/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Tags"],
    }),
    deleteTag: builder.mutation<ApiResponse, string>({
      query: (id) => ({
        url: `/tag/delete-tag/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Tags"],
    }),
  }),
});

export const {
  useCreateTagMutation,
  useGetAllTagsQuery,
  useGetTagByIdQuery,
  useUpdateTagMutation,
  useDeleteTagMutation,
} = tagsApi;
